import ReactEChartsCore from 'echarts-for-react/lib/core';
import * as echarts from 'echarts/core';
import { TooltipComponent, GridComponent, LegendComponent } from 'echarts/components';
import { BarChart } from 'echarts/charts';
import { CanvasRenderer } from 'echarts/renderers';
import { useAppContext } from '../../../../providers/AppProvider';
import { Card } from 'react-bootstrap';
import React from 'react';
import { AbuseDataItem } from './AbuseCard';

echarts.use([TooltipComponent, GridComponent, LegendComponent, BarChart, CanvasRenderer]);

interface AbuseSeverityChartProps {
  data: AbuseDataItem[];
  title: string;
  description: string;
}

const AbuseSeverityChart: React.FC<AbuseSeverityChartProps> = ({ data, title, description }) => {
  const { getThemeColor } = useAppContext();

  // Group data by category and calculate mean severity
  const categoryData = React.useMemo(() => {
    const categoryMap = new Map<string, number[]>();

    data.forEach(item => {
      const category = item.Category as string;
      const severity = (item['Severity Score'] as number) * 100; // Convert to percentage
      if (!categoryMap.has(category)) {
        categoryMap.set(category, []);
      }
      categoryMap.get(category)!.push(severity);
    });

    // Calculate mean for each category
    const result: { category: string; meanSeverity: number }[] = [];
    categoryMap.forEach((scores, category) => {
      const mean = scores.reduce((sum, score) => sum + score, 0) / scores.length;
      result.push({ category, meanSeverity: mean });
    });

    // Sort by mean severity descending
    return result.sort((a, b) => b.meanSeverity - a.meanSeverity);
  }, [data]);

  const categories = categoryData.map(item => item.category);
  const meanSeverities = categoryData.map(item => item.meanSeverity);

  // Ensure we have valid colors with fallbacks
  const primaryColor = getThemeColor('primary') || '#0d6efd';
  const bodyHighlightBg = getThemeColor('body-highlight-bg') || '#ffffff';
  const borderColor = getThemeColor('border-color') || '#dee2e6';
  const secondaryColor = getThemeColor('secondary-color') || '#6c757d';
  const lightTextEmphasis = getThemeColor('light-text-emphasis') || '#495057';

  const options = {
    color: primaryColor,
    tooltip: {
      trigger: 'axis',
      backgroundColor: bodyHighlightBg,
      borderColor: borderColor,
      textStyle: { color: lightTextEmphasis },
      borderWidth: 1,
      formatter: (params: any) => {
        const param = params[0];
        return `<strong>${param.name}:</strong> ${param.value.toFixed(1)}%`;
      }
    },
    xAxis: {
      type: 'category',
      name: 'Category',
      data: categories,
      axisLine: { lineStyle: { color: borderColor } },
      axisLabel: {
        color: secondaryColor,
        rotate: 0,
        interval: 0
      },
      nameTextStyle: { color: secondaryColor }
    },
    yAxis: {
      type: 'value',
      name: 'Mean Severity (%)',
      min: 0,
      max: Math.max(...meanSeverities, 35),
      axisLine: { lineStyle: { color: borderColor } },
      axisLabel: { color: secondaryColor },
      nameTextStyle: { color: secondaryColor }
    },
    series: [
      {
        type: 'bar',
        data: meanSeverities.map(severity => ({
          value: severity,
          itemStyle: {
            borderRadius: [5, 5, 0, 0],
            color: primaryColor
          }
        })),
        barWidth: '50%',
        label: {
          show: true,
          position: 'top',
          formatter: (params: any) => `${params.value.toFixed(1)}%`,
          color: lightTextEmphasis
        }
      }
    ],
    grid: { left: '15%', right: '10%', bottom: '15%', top: '15%' }
  };

  return (
    <Card className="h-100">
      <Card.Body>
        <div className="d-flex justify-content-between">
          <div>
            <h4 className="mb-1">{title}</h4>
            <h6 className="text-body-tertiary">{description}</h6>
          </div>
        </div>
        <div className="d-flex justify-content-center px-4 py-6">
          <ReactEChartsCore echarts={echarts} option={options} style={{ height: '400px', width: '100%' }} />
        </div>
      </Card.Body>
    </Card>
  );
};

export default AbuseSeverityChart;
